import { FC, MouseEvent, useState } from "react";
import {
  Box,
  Card,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  useMediaQuery,
} from "@material-ui/core";
import { Settings as SettingsIcon } from "@material-ui/icons";
import moment from "moment";
import { Link } from "react-router-dom";

import { AccountInfo, AlertActionArea, CardActionArea } from "components";
import { EditRoyaltyAccountModal } from "containers";
import { connectGetCredentialsRoyaltyPayorAccountDetails } from "containers/AddRoyaltyAccount/routes";
import { FormValues } from "containers/CredentialsForm";
import { queryClient } from "containers/Root";
import {
  formatAmountCompact,
  formatCurrencyAmount,
  formatCurrencyString,
} from "lib/format";
import { updateCredentialSubmissionPassword } from "services/assets";
import { ASSET_STATE_TO_USER_INFO } from "services/assets/constants";
import { AssetKYWCredentialSubmission } from "services/assets/types";
import theme, { Theme } from "theme";
import { thinBorderStyles } from "theme/constants";

const useStyles = makeStyles<Theme, { isSmDown: boolean }>(() => ({
  card: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: props => (props.isSmDown ? "unset" : "245px"),
    padding: "20px",
  },
  actionAreaContainer: thinBorderStyles,
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  menuItemLink: {
    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: theme.custom.palette.purple.lightest,
    },
  },
  editAccountBtn: {
    "&:hover": {
      backgroundColor: theme.custom.palette.purple.lightest,
    },
  },
  alertActionArea: {
    "&:hover": {
      cursor: "pointer",
    },
  },
  isLoading: {
    height: "225px",
  },
}));

export interface CredentialSubmissionCardProps {
  asset: AssetKYWCredentialSubmission;
}

const CredentialSubmissionCard: FC<CredentialSubmissionCardProps> = ({
  asset,
}) => {
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useStyles({ isSmDown });
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);

  const [
    isEditRoyaltyAccountModalOpen,
    setIsEditRoyaltyAccountModalOpen,
  ] = useState(false);

  const credentialSubmission = asset.credential_submission;
  const royaltyPayor = credentialSubmission.royalty_payor;

  const isCompleted = !!credentialSubmission?.attested_ltm;
  const isConnected =
    asset?.state === "qualified" ||
    asset?.state === "awaiting_review" ||
    asset?.state === "awaiting_valuation" ||
    asset?.state === "failed_auto_unqualified" ||
    asset?.state === "failed_unqualified" ||
    asset?.state === "undefined";

  const isMd = useMediaQuery(theme.breakpoints.only("md"));

  const credentialSubmissionStats = [
    {
      label: "Yearly Earnings",
      value: isMd
        ? formatCurrencyString(
            "USD",
            formatAmountCompact(Number(credentialSubmission?.attested_ltm)),
          )
        : formatCurrencyAmount("USD", credentialSubmission?.attested_ltm),
    },
    {
      label: "Earning Since",
      value: moment()
        .subtract(credentialSubmission?.attested_age, "years")
        .format("YYYY"),
    },
  ];

  const handleUpdatePassword = async (values: FormValues) => {
    await updateCredentialSubmissionPassword(asset.id, values.password);
    queryClient.invalidateQueries("assetKYWCredentialSubmission");
  };

  return (
    <>
      {isEditRoyaltyAccountModalOpen && (
        <EditRoyaltyAccountModal
          isOpen
          onClose={() => setIsEditRoyaltyAccountModalOpen(false)}
          onAssetEdited={() => {
            queryClient.invalidateQueries("assetKYWCredentialSubmission");
          }}
          assetIds={[asset.id]}
          hasDeal={asset.has_deal}
          username={credentialSubmission.username}
          royaltyPayor={royaltyPayor}
          handleUpdatePassword={handleUpdatePassword}
        />
      )}
      <Card elevation={3} className={classes.card}>
        <Box className={classes.header}>
          <AccountInfo
            royaltyPayorDisplayName={royaltyPayor.display_name}
            username={credentialSubmission?.username}
            asset={asset}
          />

          <Box>
            {credentialSubmission && (
              <Box width={24}>
                <IconButton
                  aria-label="more"
                  id="more-button"
                  aria-controls="asset-more-menu"
                  aria-expanded={open ? "true" : undefined}
                  aria-haspopup="true"
                  onClick={(event: MouseEvent<HTMLButtonElement>) => {
                    setAnchorElement(event.currentTarget);
                  }}
                  size="small"
                  className={classes.editAccountBtn}
                >
                  <SettingsIcon />
                </IconButton>
              </Box>
            )}
            <Menu
              id="asset-more-menu"
              anchorEl={anchorElement}
              open={Boolean(anchorElement)}
              onClose={() => setAnchorElement(null)}
            >
              {credentialSubmission && (
                <MenuItem
                  key="edit-account"
                  onClick={() => {
                    setAnchorElement(null);
                    setIsEditRoyaltyAccountModalOpen(true);
                  }}
                  dense
                  className={classes.menuItemLink}
                >
                  Edit Royalty Account
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Box>
        {asset.state === "failed_login_invalid_credentials" ? (
          <Box
            onClick={() => setIsEditRoyaltyAccountModalOpen(true)}
            className={classes.alertActionArea}
          >
            <AlertActionArea
              alertTitle={ASSET_STATE_TO_USER_INFO[asset.state].message}
              alertDetails={ASSET_STATE_TO_USER_INFO[asset.state].details}
              actionMessage="Update Now"
            />
          </Box>
        ) : asset.state === "failed_need_credentials_update" ? (
          <Link
            to={connectGetCredentialsRoyaltyPayorAccountDetails(
              royaltyPayor.name,
              asset.id.toString(),
            )}
          >
            <AlertActionArea
              alertTitle={ASSET_STATE_TO_USER_INFO[asset.state].message}
              alertDetails={ASSET_STATE_TO_USER_INFO[asset.state].details}
              actionMessage="Update Now"
            />
          </Link>
        ) : asset.state === "failed_login_2fa_block" ? (
          <AlertActionArea
            alertTitle={ASSET_STATE_TO_USER_INFO[asset.state].message}
            alertDetails={ASSET_STATE_TO_USER_INFO[asset.state].details}
          />
        ) : isConnected && !isCompleted ? (
          <Link
            to={connectGetCredentialsRoyaltyPayorAccountDetails(
              royaltyPayor.name,
              asset.id.toString(),
            )}
          >
            <AlertActionArea
              alertTitle="Continue Your Credential Submission"
              alertDetails="Finish adding the details of your Royalty Account to complete your Credential Submission and start reviewing your deal options."
              actionMessage="Continue"
            />
          </Link>
        ) : isCompleted && isConnected ? (
          <Box className={classes.actionAreaContainer}>
            <CardActionArea
              stats={credentialSubmissionStats}
              lastUpdated={
                !!credentialSubmission
                  ? moment(credentialSubmission.created_at).format("MM/DD/YY")
                  : undefined
              }
            />
          </Box>
        ) : null}
      </Card>
    </>
  );
};

export default CredentialSubmissionCard;
